import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import * as THREE from 'three';

/**
 * Class responsible for loading and caching enemy models and animations
 */
class EnemyModelLoader {
  constructor() {
    this.loader = new GLTFLoader();
    this.enemyModels = new Map();
    this.loadingPromises = new Map();
    
    // Define base path for enemy models
    this.basePath = '/assets/models/enemies/characters/gltf';
  }

  /**
   * Load an enemy model by type, with caching
   * @param {string} type - The enemy type (e.g., 'Skeleton_Mage')
   * @returns {Promise<Object>} - The loaded model and animations
   */
  async loadEnemyModel(type) {
    // Return cached model if already loaded
    if (this.enemyModels.has(type)) {
      return this.enemyModels.get(type);
    }

    // Return existing promise if already loading
    if (this.loadingPromises.has(type)) {
      return this.loadingPromises.get(type);
    }

    // Start new loading process
    const loadPromise = new Promise((resolve, reject) => {
      try {
        const modelPath = `${this.basePath}/${type}.glb`;
        console.log(`Loading enemy model from: ${modelPath}`);
        
        this.loader.load(
          modelPath,
          (gltf) => {
            // Process model
            const model = gltf.scene;
            
            // Log available animations for debugging
            console.log(`Loaded ${type} with animations:`, 
              gltf.animations.map(a => a.name));
            
            // Store in cache
            const modelData = {
              model: model,
              animations: gltf.animations
            };
            
            this.enemyModels.set(type, modelData);
            resolve(modelData);
          },
          (xhr) => {
            console.log(`${type} ${(xhr.loaded / xhr.total * 100)}% loaded`);
          },
          (error) => {
            console.error(`Error loading ${type} from ${modelPath}:`, error);
            reject(error);
          }
        );
      } catch (error) {
        console.error(`Error initializing loader for ${type}:`, error);
        reject(error);
      }
    });

    // Store loading promise in cache
    this.loadingPromises.set(type, loadPromise);
    
    try {
      const result = await loadPromise;
      // Clean up promise cache after loading
      this.loadingPromises.delete(type);
      return result;
    } catch (error) {
      this.loadingPromises.delete(type);
      throw error;
    }
  }

  /**
   * Get animation by name from loaded model
   * @param {Object} modelData - The loaded model data
   * @param {string} animationName - The animation name to find
   * @returns {THREE.AnimationClip|null} - The animation clip or null if not found
   */
  getAnimationByName(modelData, animationName) {
    if (!modelData || !modelData.animations || !modelData.animations.length) {
      return null;
    }

    // Try exact match first
    let animation = modelData.animations.find(a => a.name === animationName);
    
    // Try case-insensitive match as fallback
    if (!animation) {
      animation = modelData.animations.find(
        a => a.name.toLowerCase() === animationName.toLowerCase()
      );
    }
    
    // Try partial match as last resort
    if (!animation) {
      animation = modelData.animations.find(
        a => a.name.toLowerCase().includes(animationName.toLowerCase())
      );
    }
    
    return animation;
  }
}

export default EnemyModelLoader; 